import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('deviceTypes');
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [newDeviceType, setNewDeviceType] = useState('');
  const [deliveryUnits, setDeliveryUnits] = useState([]);
  const [newDeliveryUnit, setNewDeliveryUnit] = useState('');
  const [registeredTeamIds, setRegisteredTeamIds] = useState([]);
  const [newRegisteredTeamId, setNewRegisteredTeamId] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDeviceTypes();
    loadDeliveryUnits();
    loadRegisteredTeamIds();
  }, []);

  const loadDeviceTypes = async () => {
    try {
      const response = await settingsAPI.getDeviceTypes();
      // Handle both array response and object with deviceTypes property
      const types = Array.isArray(response.data) ? response.data : (response.data?.deviceTypes || []);
      setDeviceTypes(types);
      // Clear error messages on successful load
      setMessage(prev => {
        if (prev.type === 'error' && prev.text.includes('Failed to load device types')) {
          return { type: '', text: '' };
        }
        return prev;
      });
    } catch (error) {
      console.error('Failed to load device types:', error);
      setDeviceTypes([]);
      setMessage({
        type: 'error',
        text: 'Failed to load device types. Please refresh the page.'
      });
    }
  };

  const loadDeliveryUnits = async () => {
    try {
      const response = await settingsAPI.getDeliveryUnits();
      const units = Array.isArray(response.data) ? response.data : (response.data?.deliveryUnits || []);
      setDeliveryUnits(units);
    } catch (error) {
      console.error('Failed to load delivery units:', error);
      setDeliveryUnits([]);
    }
  };

  const loadRegisteredTeamIds = async () => {
    try {
      const response = await settingsAPI.getRegisteredTeamIds();
      const teamIds = Array.isArray(response.data) ? response.data : (response.data?.registeredTeamIds || []);
      setRegisteredTeamIds(teamIds);
    } catch (error) {
      console.error('Failed to load registered team IDs:', error);
      setRegisteredTeamIds([]);
    }
  };

  const handleAddDeviceType = async (e) => {
    e.preventDefault();
    if (!newDeviceType.trim()) {
      setMessage({ type: 'error', text: 'Device type cannot be empty' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await settingsAPI.addDeviceType(newDeviceType.trim());
      setMessage({ type: 'success', text: 'Device type added successfully!' });
      setNewDeviceType('');
      await loadDeviceTypes();
    } catch (error) {
      console.error('Failed to add device type:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to add device type'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDeviceType = async (type) => {
    if (window.confirm(`Are you sure you want to delete "${type}"?`)) {
      try {
        await settingsAPI.deleteDeviceType(type);
        setMessage({ type: 'success', text: 'Device type deleted successfully!' });
        await loadDeviceTypes();
      } catch (error) {
        console.error('Failed to delete device type:', error);
        setMessage({
          type: 'error',
          text: error.response?.data?.error || 'Failed to delete device type'
        });
      }
    }
  };
  const handleAddDeliveryUnit = async (e) => {
    e.preventDefault();
    if (!newDeliveryUnit.trim()) {
      setMessage({ type: 'error', text: 'Delivery unit cannot be empty' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await settingsAPI.addDeliveryUnit(newDeliveryUnit.trim());
      setMessage({ type: 'success', text: 'Delivery unit added successfully!' });
      setNewDeliveryUnit('');
      await loadDeliveryUnits();
    } catch (error) {
      console.error('Failed to add delivery unit:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to add delivery unit'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDeliveryUnit = async (unit) => {
    if (window.confirm(`Are you sure you want to delete "${unit}"?`)) {
      try {
        await settingsAPI.deleteDeliveryUnit(unit);
        setMessage({ type: 'success', text: 'Delivery unit deleted successfully!' });
        await loadDeliveryUnits();
      } catch (error) {
        console.error('Failed to delete delivery unit:', error);
        setMessage({
          type: 'error',
          text: error.response?.data?.error || 'Failed to delete delivery unit'
        });
      }
    }
  };

  const handleAddRegisteredTeamId = async (e) => {
    e.preventDefault();
    if (!newRegisteredTeamId.trim()) {
      setMessage({ type: 'error', text: 'Registered team ID cannot be empty' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await settingsAPI.addRegisteredTeamId(newRegisteredTeamId.trim());
      setMessage({ type: 'success', text: 'Registered team ID added successfully!' });
      setNewRegisteredTeamId('');
      await loadRegisteredTeamIds();
    } catch (error) {
      console.error('Failed to add registered team ID:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to add registered team ID'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRegisteredTeamId = async (teamId) => {
    if (window.confirm(`Are you sure you want to delete "${teamId}"?`)) {
      try {
        await settingsAPI.deleteRegisteredTeamId(teamId);
        setMessage({ type: 'success', text: 'Registered team ID deleted successfully!' });
        await loadRegisteredTeamIds();
      } catch (error) {
        console.error('Failed to delete registered team ID:', error);
        setMessage({
          type: 'error',
          text: error.response?.data?.error || 'Failed to delete registered team ID'
        });
      }
    }
  };
  return (
    <div className="card">
      <h2>Settings</h2>
      
      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        borderBottom: '2px solid #e0e0e0',
        marginTop: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <button
          onClick={() => setActiveTab('deviceTypes')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'deviceTypes' ? '#007bff' : '#f5f5f5',
            color: activeTab === 'deviceTypes' ? 'white' : '#333',
            border: '1px solid #ddd',
            borderBottom: activeTab === 'deviceTypes' ? 'none' : '1px solid #ddd',
            cursor: 'pointer',
            fontWeight: activeTab === 'deviceTypes' ? '600' : '400',
            fontSize: '0.95rem',
            borderRadius: '4px 4px 0 0',
            transition: 'all 0.2s',
            marginBottom: '-2px'
          }}
        >
          Device Types
        </button>
        <button
          onClick={() => setActiveTab('deliveryUnits')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'deliveryUnits' ? '#007bff' : '#f5f5f5',
            color: activeTab === 'deliveryUnits' ? 'white' : '#333',
            border: '1px solid #ddd',
            borderBottom: activeTab === 'deliveryUnits' ? 'none' : '1px solid #ddd',
            cursor: 'pointer',
            fontWeight: activeTab === 'deliveryUnits' ? '600' : '400',
            fontSize: '0.95rem',
            borderRadius: '4px 4px 0 0',
            transition: 'all 0.2s',
            marginBottom: '-2px'
          }}
        >
          Delivery Units
        </button>
        <button
          onClick={() => setActiveTab('teamIds')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'teamIds' ? '#007bff' : '#f5f5f5',
            color: activeTab === 'teamIds' ? 'white' : '#333',
            border: '1px solid #ddd',
            borderBottom: activeTab === 'teamIds' ? 'none' : '1px solid #ddd',
            cursor: 'pointer',
            fontWeight: activeTab === 'teamIds' ? '600' : '400',
            fontSize: '0.95rem',
            borderRadius: '4px 4px 0 0',
            transition: 'all 0.2s',
            marginBottom: '-2px'
          }}
        >
          Registered Team IDs
        </button>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Device Types Tab */}
      {activeTab === 'deviceTypes' && (
        <div className="settings-section">
          <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600 }}>Device Types</h3>
          
          <form onSubmit={handleAddDeviceType} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <input
              type="text"
              value={newDeviceType}
              onChange={(e) => setNewDeviceType(e.target.value)}
              placeholder="Enter new device type (e.g., iPhone, Android Phone)"
              style={{
                flex: 1,
                padding: '0.75rem',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem'
              }}
            />
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Adding...' : 'Add Type'}
            </button>
          </form>

          <div className="items-list">
            {deviceTypes.length === 0 ? (
              <p style={{ color: 'var(--light-text-muted)', fontStyle: 'italic' }}>No device types configured</p>
            ) : (
              deviceTypes.map((type, index) => (
                <div key={index} className="item-row">
                  <span className="item-name">{type}</span>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteDeviceType(type)}
                    style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Delivery Units Tab */}
      {activeTab === 'deliveryUnits' && (
        <div className="settings-section">
          <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600 }}>Delivery Units</h3>
          
          <form onSubmit={handleAddDeliveryUnit} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <input
              type="text"
              value={newDeliveryUnit}
              onChange={(e) => setNewDeliveryUnit(e.target.value)}
              placeholder="Enter new delivery unit (e.g., DU6, DU7)"
              style={{
                flex: 1,
                padding: '0.75rem',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem'
              }}
            />
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Adding...' : 'Add Unit'}
            </button>
          </form>

          <div className="items-list">
            {deliveryUnits.length === 0 ? (
              <p style={{ color: 'var(--light-text-muted)', fontStyle: 'italic' }}>No delivery units configured</p>
            ) : (
              deliveryUnits.map((unit, index) => (
                <div key={index} className="item-row">
                  <span className="item-name">{unit}</span>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteDeliveryUnit(unit)}
                    style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Registered Team IDs Tab */}
      {activeTab === 'teamIds' && (
        <div className="settings-section">
          <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600 }}>Registered Team IDs</h3>
          
          <form onSubmit={handleAddRegisteredTeamId} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <input
              type="text"
              value={newRegisteredTeamId}
              onChange={(e) => setNewRegisteredTeamId(e.target.value)}
              placeholder="Enter registered team ID"
              style={{
                flex: 1,
                padding: '0.75rem',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem'
              }}
            />
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Adding...' : 'Add Team ID'}
            </button>
          </form>

          <div className="items-list">
            {registeredTeamIds.length === 0 ? (
              <p style={{ color: 'var(--light-text-muted)', fontStyle: 'italic' }}>No registered team IDs configured</p>
            ) : (
              registeredTeamIds.map((teamId, index) => (
                <div key={index} className="item-row">
                  <span className="item-name">{teamId}</span>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteRegisteredTeamId(teamId)}
                    style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
